/**
 * SEO components for category landing pages
 */

import React from 'react';
import { Helmet } from 'react-helmet-async';
import { getCategoryInfo } from '@/utils/filterUtils';

interface CategorySEOProps {
  type: 'software' | 'holiday' | 'genre' | 'artist';
  value: string;
  totalResults?: number;
  pathname: string;
}

export const CategorySEO: React.FC<CategorySEOProps> = ({
  type,
  value,
  totalResults = 0,
  pathname
}) => {
  const categoryInfo = getCategoryInfo(type as 'software' | 'holiday', value);
  const currentUrl = `${window.location.origin}${pathname}`;
  
  // Generate structured data for breadcrumbs
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": window.location.origin
      },
      {
        "@type": "ListItem", 
        "position": 2,
        "name": "Sequences",
        "item": `${window.location.origin}/sequences`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": categoryInfo.title,
        "item": currentUrl
      }
    ]
  };

  // Generate collection page schema
  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": categoryInfo.title,
    "description": categoryInfo.description,
    "url": currentUrl,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": totalResults,
      "name": `${categoryInfo.title} Collection`
    },
    "breadcrumb": breadcrumbSchema
  };

  return (
    <Helmet>
      {/* Basic meta tags */}
      <title>{`${categoryInfo.title} | Light Hunt`}</title>
      <meta name="description" content={categoryInfo.description} />
      <meta name="keywords" content={categoryInfo.keywords.join(', ')} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Open Graph tags */}
      <meta property="og:title" content={categoryInfo.title} />
      <meta property="og:description" content={categoryInfo.description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Light Hunt" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={categoryInfo.title} />
      <meta name="twitter:description" content={categoryInfo.description} />
      
      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Structured data */}
      <script type="application/ld+json">
        {JSON.stringify(collectionSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};